import React, { Component } from 'react'
import { DraggableCore } from 'react-draggable'
import { throttle } from 'lodash'
import copy from 'fast-copy'
import moment from 'moment'

import GanttChartTrack from './gantt-chart-track'

import { 
	momentToApiTimeFormat,
	getPrimaryColumnValue
} from 'utils'

export default class GanttChart extends Component {
    state = {
        scrollLeft: 0,
        datesHeight: 60,
        isDragging: false,
        interactedTaskId: null,
    }

    UNSAFE_componentWillMount() {
        this.throttleHandleDragOnChart = throttle(this.throttleHandleDragOnChart, 20)   
	} 

    initialMousePoint = null
    previousDragOffset = 0

	handleDragOnChart = (type, e) => {
        if(this.props.blockPan){
            return 
		}

		switch(type){
			case 'drag':
				if(!this.initialMousePoint){
					this.initialMousePoint = e.clientX
				}else{
					const delta = e.clientX - this.initialMousePoint
					const amountToMove = delta - this.previousDragOffset

					const hasPreviousDragOffset = !!this.previousDragOffset

					this.previousDragOffset = delta

                    const newScrollLeft = this.state.scrollLeft - amountToMove

					if(this.isNewScrollLeftValid(newScrollLeft) && hasPreviousDragOffset){
						this.setState({
							scrollLeft: newScrollLeft,
							isDragging: true
						})
					}
				}

				break
			case 'end':
				this.initialMousePoint = null	
				this.previousDragOffset = 0		

				this.setState({
					isDragging: false
				})

				break
		}

    }
    
    throttleHandleDragOnChart = e => {
        this.handleDragOnChart('drag', e)
    }

	isNewScrollLeftValid = scrollLeft => {
		if(scrollLeft < 0){
			return false
		}

		const maxScroll = this.props.chartRefs.inner.clientWidth - this.props.chartRefs.outer.clientWidth

		if(scrollLeft > maxScroll){
			return false
		}

		return true
	}

	toggleDragOrResize = (startOrStop, e, taskId) => {
		switch(startOrStop){
			case 'start':
				e.stopPropagation()
                this.setState({
                    interactedTaskId: taskId
                })
				break
			case 'stop':
				clearInterval(this.scrollInterval)
				this.scrollInterval = null
				break
		}
	}

	draggedTaskLastX = null

	handleMoveTask = (operation, taskId, data) => {
		const {
			currentProject,
			updateTask,
			cellWidth,
			allTasks
		} = this.props

		if(operation == 'start'){
			this.draggedTaskLastX = data.lastX
		}else{
			const normalisedDelta = (data.lastX - this.draggedTaskLastX)/cellWidth
			this.draggedTaskLastX = null

			const task = allTasks[taskId]

			let tempTask = copy(task)
			
			const taskDates = getPrimaryColumnValue('date', task, currentProject)

			taskDates.start = taskDates.start ? momentToApiTimeFormat(moment(taskDates.start).add(normalisedDelta, 'days')) : null
			taskDates.end = taskDates.end ? momentToApiTimeFormat(moment(taskDates.end).add(normalisedDelta, 'days')) : null
			
			let primaryDateColumnId = null

			currentProject.columns.forEach(column => {
				if(column.type == 'DATE' && column.is_primary){
					primaryDateColumnId = column.id
				}
			})

			tempTask.column_data[primaryDateColumnId] = taskDates

            updateTask(tempTask)
            
            this.forceUpdate()
		}
	}

	dragScrollIndicators = {}
	scrollInterval = null
	scrollIntervalDirection = null
    scrollIntervalIntense = false
    
    setInteractedTaskId = id => {
        this.setState({
            interactedTaskId: id
        })
    }

	handleMouseMove = e => {
		if(this.state.interactedTaskId){
			const leftRect = this.dragScrollIndicators.left.getBoundingClientRect()
			const rightRect = this.dragScrollIndicators.right.getBoundingClientRect()

			const mouseX = e.screenX

			this.scrollIntervalIntense = false

			if(mouseX < (leftRect.left + leftRect.width)){
				if(mouseX < leftRect.left + (leftRect.width/2)){
					this.scrollIntervalIntense = true
				}

				this.startScrolling('left')
			}else if(mouseX > rightRect.left){
				if(mouseX > rightRect.left + (rightRect.width/2)){
					this.scrollIntervalIntense = true
				}

				this.startScrolling('right')
			}else{
				this.toggleDragOrResize('stop')
			}
		}
	}

	startScrolling = direction => {
		if(!this.scrollInterval){
			this.scrollInterval = setInterval(_ => {
				let newScrollLeft = this.state.scrollLeft

				// go extra quick if you take it nearer to the edge
				const delta = this.scrollIntervalIntense ? 20 : 10

				switch(direction){
					case 'left':
						newScrollLeft -= delta
						break
					case 'right':
						newScrollLeft += delta
						break
				}

				if(this.isNewScrollLeftValid(newScrollLeft)){
					this.setState({
						scrollLeft: newScrollLeft
					})
				}
			}, 50)
		}
	}
	
	setScrollLeft = value => {
		const maxScroll = this.props.chartRefs.inner.clientWidth - this.props.chartRefs.outer.clientWidth

		this.setState({
			scrollLeft: value > maxScroll ? maxScroll : value
		})
	}

    render() {
        const {
			currentProject,
            ganttData,
            cellWidth,
            chartRefs,
            isMilestoneCollapsed,
            zoomLevelIndex,
            handleResizeTask,
			columnData,
			collapsedMilestones
        } = this.props

        const {
            isDragging,
            scrollLeft
		} = this.state

        const chartRefOffsets = {}

        if(chartRefs && chartRefs.outer){
            chartRefOffsets.left = chartRefs.outer.getBoundingClientRect().left
            chartRefOffsets.right = chartRefs.outer.getBoundingClientRect().right
		}

		let ganttDataClassName = 'gantt-data'

		if(isDragging){
			ganttDataClassName += ' dragging'
		}

        return (
            <DraggableCore
                onDrag={this.throttleHandleDragOnChart}
                onStop={e => {
                    this.handleDragOnChart('end', e)
                }}
            >
                <ul
                    className={ganttDataClassName}
                    ref={ref => chartRefs.outer = ref}
                    onMouseMove={this.handleMouseMove}
                >
                    <div className="drag-scroll-indicators">
                        <div ref={ref => this.dragScrollIndicators.left = ref} />
                        <div ref={ref => this.dragScrollIndicators.right = ref} />
                    </div>
                    <div
                        style={{
                            transform: 'translateX(-' + scrollLeft + 'px)',
                            width: ganttData ? (ganttData.totalDateRange * cellWidth) + 'px' : 0
                        }}
                    >
                        <GanttChartTrack
							currentProject={currentProject}
                            columnData={columnData}
                            ganttData={ganttData}
                            isMilestoneCollapsed={isMilestoneCollapsed}
                            zoomLevelIndex={zoomLevelIndex}
                            handleResizeTask={handleResizeTask}
                            handleMoveTask={this.handleMoveTask}
                            interactedTaskId={this.state.interactedTaskId}
                            setInteractedTaskId={this.setInteractedTaskId}
                            toggleDragOrResize={this.toggleDragOrResize}
                            chartRefOffsets={chartRefOffsets}
                            cellWidth={cellWidth}
                            isDragging={isDragging}
                            chartRefs={chartRefs}
							ref={ref => this.chartTrackRef = ref}
							collapsedMilestones={collapsedMilestones}
                        />
                    </div>
                </ul>
            </DraggableCore>
        )
    }
}